import { type FC } from "react";

import { ResponseType, useResponsesForPage } from "./responses";

import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { NewSubmissionBadge } from "components/materials/page/assessment/NewSubmissionBadge";
import { getOfficialSubmission, getSubmissionsByAccessId } from "components/materials/page/helpers";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import type { SubmissionType } from "components/server-types";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { CircleExclamationIcon, PersonGroupIcon, PersonSingleIcon, VerifiedIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  sectionUserSelectors,
  selectSortedSubmissionsByPageId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";
import { isGroupPage } from "utils/page-utils";
import { useQueryParamsActions } from "utils/routing";

interface StudentDropdownItemIconParams {
  submissions: SubmissionType[];
  isGroup: boolean;
}

const StudentDropdownItemIcon: FC<StudentDropdownItemIconParams> = ({ submissions, isGroup }) => {
  if (submissions.length === 0) {
    return (
      <div className="text-orange">
        <CircleExclamationIcon />
      </div>
    );
  }

  if (getOfficialSubmission(submissions)) {
    return <VerifiedIcon className="text-green" />;
  }

  return isGroup ? <PersonGroupIcon /> : <PersonSingleIcon />;
};

interface StudentDropdownProps {
  pageId: string;
}

export const StudentDropdown: FC<StudentDropdownProps> = ({ pageId }) => {
  const { setQueryParams, deleteQueryParams } = useQueryParamsActions();
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  const sectionUsers = useListSelector(sectionUserSelectors.filterForCurrentSection);
  const submissions = useAppSelector((s) => selectSortedSubmissionsByPageId(s, pageId));
  const { responses } = useResponsesForPage(pageId);

  const isAssessing = useIsAssessing();
  const isLargeScreen = useIsLgOrLarger();
  const selectedResponse = useFindItemBySearchParamId(responses, ACCESS_ID_QUERY_PARAM, true);

  if (!page || !selectedResponse || (isAssessing && isLargeScreen)) {
    return null;
  }

  const isGroup = isGroupPage(page);

  const onSelectionChange = (item: ResponseType) => {
    if (item.id) {
      setQueryParams({ [ACCESS_ID_QUERY_PARAM]: item.id, [SUBMISSION_ID_QUERY_PARAM]: null });
    } else {
      deleteQueryParams([ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM]);
    }
  };

  const items: ListboxOption[] = responses.map((response) => {
    const studentSubmissions = getSubmissionsByAccessId(response.id, sectionUsers, submissions);

    return {
      id: response.id,
      title: response.title,
      iconEnd: <NewSubmissionBadge sortedSubmissions={studentSubmissions} />,
      iconStart: <StudentDropdownItemIcon isGroup={isGroup} submissions={studentSubmissions} />,
    };
  });

  return (
    <Listbox
      buttonSize="s"
      items={items}
      itemSize="xs"
      selectedValue={selectedResponse.id}
      onSelectionChange={onSelectionChange}
    />
  );
};
